import React, { ReactNode, ElementType } from 'react';
import { ButtonProps } from '../Button';
import Box, { ExtendableBoxProps, BoxProps } from '../Box';

import {
  DeprecatedAndDangerousStylingProps,
  processStylingProps,
} from '../_internal/styling';
import ButtonLockup from '../ButtonLockup';
import ButtonBase from '../ButtonBase';

type ActionProps = Partial<ButtonProps> & {
  text: ReactNode;
};

interface Props extends DeprecatedAndDangerousStylingProps {
  /**
   * The primary action button, consisting of `text` (alias for `children`)
   * and usually an 'onClick' callback. This should be used as the primary
   * CTA and close the modal on success. Extends `ButtonProps` to customize
   * the button.
   */
  primaryAction: ActionProps;
  /**
   * The (optional) secondary action button, consisting of `text` and
   * usually an `onClick` callback. Often a cancel/back action to close
   * the modal.  Extends `ButtonProps` to customize the link.
   */
  secondaryAction?: ActionProps;
}

export type ModalFooterProps<C extends ElementType = 'footer'> =
  ExtendableBoxProps<C, Props>;

const ModalFooter = <C extends ElementType = 'footer'>({
  primaryAction,
  secondaryAction,
  ...rootProps
}: ModalFooterProps<C>) => {
  if (!primaryAction && !secondaryAction) {
    return null;
  }

  // TODO: we need to improve processStylingProps to create types that don't
  // trigger `neither type sufficiently overlaps with the other` errors
  const boxProps = processStylingProps(rootProps, 'ModalFooter', {
    stylingProps: 'warn',
    dangerousStylingProps: 'rewrite',
  }) as unknown as BoxProps<C>;
  const primary = {
    ...primaryAction,
    children: primaryAction.children || primaryAction.text,
  };
  const tertiary = secondaryAction && {
    as: ButtonBase,
    ...secondaryAction,
    children: secondaryAction.children || secondaryAction.text,
  };

  // ideally the `ButtonLockup` would *be* the `ModalFooter` but because
  // of the (likely unnecessary) spread props, the `Box` wrapper is needed
  // for backwards compatibility.

  return (
    <Box {...boxProps}>
      <ButtonLockup primary={primary} tertiary={tertiary} />
    </Box>
  );
};

export default ModalFooter;
